// Starting a DM: pick 1–9 people from this server (search by name), then open the conversation.
import { useEffect, useMemo, useState } from "react";
import { MAX_DM_MEMBERS, type Member } from "@bloom/shared";
import { isNarrow, isTouch } from "../mobile.ts";
import { Avatar } from "./ui.tsx";
import { Sheet } from "./Sheet.tsx";
import "./dms.css";
import { CloseButton, CloseIcon } from "./icons.tsx";

const MAX_OTHERS = MAX_DM_MEMBERS - 1;

export function NewDmPicker({ members, me, online, onClose, onOpen }: {
  members: Member[];
  me: string;
  online: Set<string>;
  onClose: () => void;
  /** Opens the DM; resolves to an error message, or null once it's open. */
  onOpen: (userIds: string[]) => Promise<string | null>;
}) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sheet = isNarrow();

  const others = useMemo(() => members.filter((m) => m.id !== me), [members, me]);
  const q = query.trim().toLowerCase();
  // Names that start with the query first, then ones that contain it.
  const matches = q
    ? [...others.filter((m) => m.name.toLowerCase().startsWith(q)), ...others.filter((m) => !m.name.toLowerCase().startsWith(q) && m.name.toLowerCase().includes(q))]
    : others;
  const full = picked.length >= MAX_OTHERS;

  const toggle = (id: string) => {
    setError(null);
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= MAX_OTHERS ? p : [...p, id]));
  };
  const go = async (ids = picked) => {
    if (!ids.length || busy) return;
    setBusy(true);
    const err = await onOpen(ids);
    setBusy(false);
    if (err) setError(err);
  };

  // Desktop: Esc closes (the phone sheet handles its own).
  useEffect(() => {
    if (sheet) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheet]);

  const nameOf = (id: string) => others.find((m) => m.id === id)?.name ?? "Someone";
  const body = (
    <div className="dm-picker">
      <div className="dm-picker-head">
        <h2 id="dm-picker-title">New message</h2>
        {!sheet && <CloseButton onClick={onClose} aria-label="Close" />}
      </div>
      <div className="dm-picker-search">
        {picked.map((id) => (
          <button key={id} type="button" className="dm-chip" onClick={() => toggle(id)} aria-label={`Remove ${nameOf(id)}`}>
            {nameOf(id)} <CloseIcon size={12} />
          </button>
        ))}
        <input
          className="dm-picker-input"
          placeholder={picked.length ? "Add someone…" : "Find people"}
          aria-label="Find people"
          value={query}
          autoFocus={!isTouch()}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !query && picked.length) setPicked((p) => p.slice(0, -1));
            if (e.key === "Enter") {
              e.preventDefault();
              // Enter with a search picks the best match; with an empty box it opens the DM.
              if (q && matches[0]) {
                if (!picked.includes(matches[0].id)) toggle(matches[0].id);
                setQuery("");
              } else void go();
            }
          }}
        />
      </div>
      <div className="dm-picker-list sheet-scroll" role="listbox" aria-multiselectable="true" aria-labelledby="dm-picker-title">
        {matches.map((m) => {
          const on = picked.includes(m.id);
          return (
            // With nobody picked yet, tapping a person opens the DM at once (one tap, like a channel); the box at the
            // right picks people for a group DM, and once someone is picked a tap picks more.
            <div key={m.id} className="dm-row" role="none">
              <button type="button" role="option" aria-selected={on} className={`dm-option${on ? " on" : ""}`} disabled={!on && full} onClick={() => (picked.length ? toggle(m.id) : void go([m.id]))}>
                <Avatar id={m.id} name={m.name} size={28} presence={online.has(m.id) ? "online" : undefined} />
                <span className="dm-option-name">{m.name}</span>
              </button>
              <button type="button" className={`dm-check${on ? " on" : ""}`} aria-pressed={on} aria-label={`${on ? "Remove" : "Add"} ${m.name} ${on ? "from" : "to"} a group message`} disabled={!on && full} onClick={() => toggle(m.id)}>{on ? "✓" : ""}</button>
            </div>
          );
        })}
        {!matches.length && <p className="muted" style={{ padding: "8px 12px", margin: 0, fontSize: 13 }}>{others.length ? "Nobody by that name here." : "Invite someone first: nobody else is in this server yet."}</p>}
      </div>
      <div className="dm-picker-foot">
        <span className={error ? "error" : "muted"} style={{ fontSize: 12, flex: 1 }} role={error ? "alert" : undefined}>
          {error ?? (full ? `Up to ${MAX_OTHERS} people.` : picked.length > 1 ? "Starts a group DM." : "")}
        </span>
        <button className="btn btn-primary btn-small dm-go" disabled={!picked.length || busy} onClick={() => void go()}>
          {picked.length > 1 ? "Start group DM" : "Message"}
        </button>
      </div>
    </div>
  );

  return sheet ? (
    <Sheet label="New message" onClose={onClose}>{body}</Sheet>
  ) : (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal dm-modal" role="dialog" aria-modal="true" aria-labelledby="dm-picker-title">{body}</div>
    </div>
  );
}
